import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { DifficultyLevel } from '../types';
import { Button } from '../components/shared/Button';
import './Setup.css';

const Setup: React.FC = () => {
  const navigate = useNavigate();
  const { teams, addTeam, removeTeam, clearTeams, updateTeam } = useGame();
  const [teamName, setTeamName] = useState('');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('original');
  const [startYear, setStartYear] = useState<number>(1980);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [editingYear, setEditingYear] = useState<number>(1980);

  const handleAddTeam = (e: React.FormEvent) => {
    e.preventDefault();
    if (teamName.trim() && teams.length < 12) {
      addTeam(teamName.trim(), difficulty, startYear);
      setTeamName('');
      // Generate a new random start year for the next team
      setStartYear(Math.floor(Math.random() * (2020 - 1960 + 1)) + 1960);
    }
  };

  const getDifficultyLabel = (diff: DifficultyLevel): string => {
    switch (diff) {
      case 'original':
        return 'Original (2 tokens)';
      case 'pro':
        return 'Pro (5 tokens)';
      case 'expert':
        return 'Expert (3 tokens)';
    }
  };

  const getDifficultyColor = (diff: DifficultyLevel): string => {
    switch (diff) {
      case 'original':
        return '#10b981';
      case 'pro':
        return '#3b82f6';
      case 'expert':
        return '#f59e0b';
    }
  };

  return (
    <div className="setup-page">
      <div className="setup-container">
        <h1>🎵 Hitster Buurtgebouw</h1>
        <p className="subtitle">Muziekquiz voor het hele buurtgebouw!</p>

        <div className="setup-content">
          <div className="setup-form-section">
            <h2>Team Toevoegen</h2>
            <form onSubmit={handleAddTeam} className="team-form">
              <div className="form-group">
                <label htmlFor="teamName">Team Naam</label>
                <input
                  type="text"
                  id="teamName"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Bijv. De Muzikanten"
                  maxLength={30}
                />
              </div>

              <div className="form-group">
                <label htmlFor="difficulty">Moeilijkheidsgraad</label>
                <select
                  id="difficulty"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as DifficultyLevel)}
                >
                  <option value="original">Original - Alleen positie (2 tokens)</option>
                  <option value="pro">Pro - Positie + Artiest + Titel (5 tokens)</option>
                  <option value="expert">Expert - Exact jaar + Artiest + Titel (3 tokens)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="startYear">Startjaar (eerste kaart)</label>
                <input
                  type="number"
                  id="startYear"
                  value={startYear}
                  onChange={(e) => setStartYear(parseInt(e.target.value) || 1980)}
                  min={1900}
                  max={2025}
                />
                <small className="form-hint">Elk team start met één kaart uit dit jaar</small>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={!teamName.trim() || teams.length >= 12}
              >
                + Team Toevoegen
              </Button>
            </form>

            <div className="team-count">
              {teams.length}/12 teams toegevoegd
            </div>
          </div>

          <div className="teams-section">
            <div className="teams-header">
              <h2>Teams ({teams.length})</h2>
              {teams.length > 0 && (
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => {
                    if (window.confirm('Weet je zeker dat je alle teams wilt wissen?')) {
                      clearTeams();
                    }
                  }}
                >
                  🗑️ Wis Teams
                </Button>
              )}
            </div>
            {teams.length === 0 ? (
              <div className="empty-state">
                <p>Nog geen teams toegevoegd</p>
                <p>Voeg minimaal 2 teams toe om te starten</p>
              </div>
            ) : (
              <div className="team-grid">
                {teams.map((team) => (
                  <div key={team.id} className="team-card">
                    <div className="team-card-header">
                      <h3>{team.name}</h3>
                      <button
                        className="remove-btn"
                        onClick={() => removeTeam(team.id)}
                        aria-label="Verwijder team"
                      >
                        ×
                      </button>
                    </div>
                    <div
                      className="team-difficulty"
                      style={{ backgroundColor: getDifficultyColor(team.difficulty) }}
                    >
                      {getDifficultyLabel(team.difficulty)}
                    </div>
                    <div className="team-timeline">
                      <span className="timeline-label">📅 Timeline:</span>
                      <div className="timeline-cards">
                        {(team.timeline || []).map((card, i) => (
                          card.isStart && editingTeamId === team.id ? (
                            <div key={i} className="timeline-edit">
                              <input
                                type="number"
                                value={editingYear}
                                onChange={(e) => setEditingYear(parseInt(e.target.value) || 1980)}
                                min={1900}
                                max={2025}
                                className="year-edit-input"
                                autoFocus
                              />
                              <button
                                className="year-save-btn"
                                onClick={() => {
                                  updateTeam(team.id, {
                                    timeline: team.timeline.map(c => 
                                      c.isStart ? { ...c, year: editingYear } : c
                                    )
                                  });
                                  setEditingTeamId(null);
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="year-cancel-btn"
                                onClick={() => setEditingTeamId(null)}
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <span 
                              key={i} 
                              className={`timeline-card ${card.isStart ? 'start-card editable' : ''}`}
                              title={card.isStart ? 'Klik om te bewerken' : (card.title ? `${card.title} - ${card.artist}` : '')}
                              onClick={() => {
                                if (card.isStart) {
                                  setEditingTeamId(team.id);
                                  setEditingYear(card.year);
                                }
                              }}
                            >
                              {card.year}
                              {card.isStart && <span className="edit-hint">✎</span>}
                            </span>
                          )
                        ))}
                      </div>
                    </div>
                    <div className="team-tokens">
                      {Array.from({ length: team.tokens }).map((_, i) => (
                        <span key={i} className="token-icon">🪙</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="setup-actions">
          <Button
            variant="success"
            size="large"
            onClick={() => navigate('/prepare')}
            disabled={teams.length < 2}
          >
            Start Spel! →
          </Button>
          {teams.length < 2 && (
            <p className="helper-text">Voeg nog {2 - teams.length} team(s) toe om te starten</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Setup;
